import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Gamepad2, 
  Play, 
  Users, 
  Monitor, 
  Trophy, 
  Coins,
  ArrowLeft,
  History,
  Star,
  Zap
} from "lucide-react";

// Renk seçenekleri
const HAIR_COLORS = [
  { id: "black", color: "#1a1a1a", name: "Siyah" },
  { id: "brown", color: "#8B4513", name: "Kahverengi" },
  { id: "blonde", color: "#FFD700", name: "Sarı" },
  { id: "red", color: "#DC143C", name: "Kızıl" },
  { id: "gray", color: "#808080", name: "Gri" },
  { id: "blue", color: "#1E90FF", name: "Mavi" },
];

const SHIRT_COLORS = [
  { id: "red", color: "#dc2626", name: "Kırmızı" },
  { id: "blue", color: "#2563eb", name: "Mavi" },
  { id: "green", color: "#16a34a", name: "Yeşil" },
  { id: "yellow", color: "#eab308", name: "Sarı" },
  { id: "white", color: "#ffffff", name: "Beyaz" },
  { id: "black", color: "#1f2937", name: "Siyah" },
  { id: "orange", color: "#f97316", name: "Turuncu" },
  { id: "purple", color: "#9333ea", name: "Mor" },
];

const HeadBallPage = ({ user, onLogout, refreshUser }) => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  const keysRef = useRef({});
  const gameStateRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState("play");
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [myGames, setMyGames] = useState([]);
  const [gameStats, setGameStats] = useState({ wins: 0, losses: 0, draws: 0, total: 0 });
  const [waitingGames, setWaitingGames] = useState([]);
  
  // Oyun durumu
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [gameMode, setGameMode] = useState(null);
  const [playerConfig, setPlayerConfig] = useState({
    hairColor: "#1a1a1a",
    shirtColor: "#2563eb"
  });
  
  // Aktif oyun
  const [currentGame, setCurrentGame] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameTime, setGameTime] = useState(180);
  const [score, setScore] = useState({ player1: 0, player2: 0 });
  const [gameEnded, setGameEnded] = useState(false);
  
  // Mobil kontrol
  const [isMobile, setIsMobile] = useState(false);
  
  // Mobil cihaz kontrolü
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Mobil kontrol fonksiyonları
  const handleTouchStart = (key) => {
    keysRef.current[key] = true;
  };
  
  const handleTouchEnd = (key) => {
    keysRef.current[key] = false;
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Klavye olayları için useEffect
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['w', 'W', 'a', 'A', 's', 'S', 'd', 'D', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        keysRef.current[e.key] = true;
      }
    };
    
    const handleKeyUp = (e) => {
      keysRef.current[e.key] = false;
    };
    
    if (gameStarted && !gameEnded) {
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameStarted, gameEnded]);

  // Oyun döngüsü için useEffect
  useEffect(() => {
    if (!gameStarted || !currentGame || gameEnded) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Oyun state'i - Gerçek Head Ball mantığı
    const groundY = height - 40; // Zemin seviyesi
    const goalHeight = 100; // Kale yüksekliği
    const goalWidth = 20; // Kale genişliği
    
    gameStateRef.current = {
      player1: {
        x: width * 0.25,
        y: groundY - 25, // Zemin seviyesinde
        vx: 0,
        vy: 0,
        radius: 25,
        color: currentGame.player1Config?.shirtColor || "#2563eb",
        hairColor: currentGame.player1Config?.hairColor || "#1a1a1a",
        onGround: true
      },
      player2: {
        x: width * 0.75,
        y: groundY - 25,
        vx: 0,
        vy: 0,
        radius: 25,
        color: currentGame.player2Config?.shirtColor || "#dc2626",
        hairColor: currentGame.player2Config?.hairColor || "#8B4513",
        onGround: true,
        isPC: currentGame.isVsPC
      },
      ball: {
        x: width / 2,
        y: groundY - 100, // Başlangıçta havada
        vx: 0,
        vy: 0,
        radius: 12
      },
      groundY: groundY,
      goalHeight: goalHeight,
      goalWidth: goalWidth,
      score: { player1: 0, player2: 0 },
      time: 180
    };
    
    let lastTime = 0;
    let timeAccumulator = 0;
    
    const gameLoop = (timestamp) => {
      if (!gameStateRef.current) return;
      
      if (!lastTime) lastTime = timestamp;
      const dt = Math.min((timestamp - lastTime) / 1000, 0.1);
      lastTime = timestamp;
      
      const state = gameStateRef.current;
      const keys = keysRef.current;
      const groundY = state.groundY;
      const goalH = state.goalHeight;
      const goalW = state.goalWidth;
      
      // Zaman sayacı
      timeAccumulator += dt;
      if (timeAccumulator >= 1) {
        state.time -= 1;
        timeAccumulator = 0;
        setGameTime(state.time);
        
        if (state.time <= 0 || state.score.player1 >= 5 || state.score.player2 >= 5) {
          finishGame(currentGame.id, state.score.player1, state.score.player2);
          return;
        }
      }
      
      // Oyuncu 1 kontrolü (WASD)
      if (keys['a'] || keys['A']) state.player1.vx = -5;
      else if (keys['d'] || keys['D']) state.player1.vx = 5;
      else state.player1.vx *= 0.8;
      
      if ((keys['w'] || keys['W']) && state.player1.onGround) {
        state.player1.vy = -12;
        state.player1.onGround = false;
      }
      
      // PC AI veya Oyuncu 2
      if (state.player2.isPC) {
        const ballDist = state.ball.x - state.player2.x;
        if (Math.abs(ballDist) > 20) {
          state.player2.vx = ballDist > 0 ? 4 : -4;
        } else {
          state.player2.vx *= 0.8;
        }
        
        // AI zıplama - top yakınsa ve havadaysa
        if (state.ball.y < state.player2.y && state.player2.onGround && 
            Math.abs(state.ball.x - state.player2.x) < 100 && Math.random() > 0.95) {
          state.player2.vy = -10;
          state.player2.onGround = false;
        }
      } else {
        if (keys['ArrowLeft']) state.player2.vx = -5;
        else if (keys['ArrowRight']) state.player2.vx = 5;
        else state.player2.vx *= 0.8;
        
        if (keys['ArrowUp'] && state.player2.onGround) {
          state.player2.vy = -12;
          state.player2.onGround = false;
        }
      }
      
      // Fizik sabitleri
      const gravity = 0.5;
      
      // Oyuncu 1 fiziği
      state.player1.vy += gravity;
      state.player1.x += state.player1.vx;
      state.player1.y += state.player1.vy;
      
      // Zemin kontrolü
      if (state.player1.y > groundY - state.player1.radius) {
        state.player1.y = groundY - state.player1.radius;
        state.player1.vy = 0;
        state.player1.onGround = true;
      }
      // Oyuncu 1 sınırları
      state.player1.x = Math.max(goalW + state.player1.radius, Math.min(width / 2 - state.player1.radius, state.player1.x));
      
      // Oyuncu 2 fiziği
      state.player2.vy += gravity;
      state.player2.x += state.player2.vx;
      state.player2.y += state.player2.vy;
      
      if (state.player2.y > groundY - state.player2.radius) {
        state.player2.y = groundY - state.player2.radius;
        state.player2.vy = 0;
        state.player2.onGround = true;
      }
      state.player2.x = Math.max(width / 2 + state.player2.radius, Math.min(width - goalW - state.player2.radius, state.player2.x));
      
      // Top fiziği
      state.ball.vy += gravity * 0.4;
      state.ball.x += state.ball.vx;
      state.ball.y += state.ball.vy;
      
      // Sol duvar çarpışması (kale dışı)
      if (state.ball.x < state.ball.radius + goalW) {
        // Kale bölgesi: groundY - goalH ile groundY arası
        const inGoalArea = state.ball.y > groundY - goalH && state.ball.y < groundY;
        if (!inGoalArea) {
          state.ball.x = state.ball.radius + goalW;
          state.ball.vx *= -0.7;
        }
      }
      
      // Sağ duvar çarpışması (kale dışı)
      if (state.ball.x > width - state.ball.radius - goalW) {
        const inGoalArea = state.ball.y > groundY - goalH && state.ball.y < groundY;
        if (!inGoalArea) {
          state.ball.x = width - state.ball.radius - goalW;
          state.ball.vx *= -0.7;
        }
      }
      
      // Üst duvar
      if (state.ball.y < state.ball.radius) {
        state.ball.y = state.ball.radius;
        state.ball.vy *= -0.7;
      }
      
      // Zemin - top zıplasın (oyuncularla aynı seviyede)
      if (state.ball.y > groundY - state.ball.radius) {
        state.ball.y = groundY - state.ball.radius;
        state.ball.vy *= -0.55;
        state.ball.vx *= 0.9;
      }
      
      // Top hızını sınırla - daha yüksek limit
      const maxSpeed = 15;
      if (Math.abs(state.ball.vx) > maxSpeed) state.ball.vx = maxSpeed * Math.sign(state.ball.vx);
      if (Math.abs(state.ball.vy) > maxSpeed) state.ball.vy = maxSpeed * Math.sign(state.ball.vy);
      
      // Top-oyuncu çarpışması
      const checkCollision = (player) => {
        const dx = state.ball.x - player.x;
        const dy = state.ball.y - player.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < state.ball.radius + player.radius) {
          const angle = Math.atan2(dy, dx);
          const speed = Math.sqrt(state.ball.vx * state.ball.vx + state.ball.vy * state.ball.vy);
          
          // Vuruş kuvveti - daha güçlü yukarı kaldırma
          const hitPower = Math.min(speed + 6, 12);
          state.ball.vx = Math.cos(angle) * hitPower;
          state.ball.vy = Math.sin(angle) * hitPower - 8; // Daha fazla yukarı kuvvet
          
          // Topun içine girmesini engelle
          state.ball.x = player.x + Math.cos(angle) * (state.ball.radius + player.radius + 1);
          state.ball.y = player.y + Math.sin(angle) * (state.ball.radius + player.radius + 1);
        }
      };
      
      checkCollision(state.player1);
      checkCollision(state.player2);
      
      // Gol kontrolü - kaleler altta (groundY - goalH ile groundY arası)
      // Sol kaleye gol (Oyuncu 2 gol attı)
      if (state.ball.x < goalW && state.ball.y > groundY - goalH) {
        state.score.player2++;
        setScore({ player1: state.score.player1, player2: state.score.player2 });
        // Top ortaya gelsin
        state.ball.x = width / 2;
        state.ball.y = groundY - 80;
        state.ball.vx = 0;
        state.ball.vy = 0;
      }
      
      // Sağ kaleye gol (Oyuncu 1 gol attı)
      if (state.ball.x > width - goalW && state.ball.y > groundY - goalH) {
        state.score.player1++;
        setScore({ player1: state.score.player1, player2: state.score.player2 });
        // Top ortaya gelsin
        state.ball.x = width / 2;
        state.ball.y = groundY - 80;
        state.ball.vx = 0;
        state.ball.vy = 0;
      }
      
      // Çizim
      drawGame(ctx, state, width, height);
      
      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };
    
    gameLoopRef.current = requestAnimationFrame(gameLoop);
    
    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [gameStarted, currentGame, gameEnded]);

  const drawGame = (ctx, state, width, height) => {
    const groundY = state.groundY;
    const goalH = state.goalHeight;
    const goalW = state.goalWidth;
    
    // Gökyüzü gradyanı
    const skyGradient = ctx.createLinearGradient(0, 0, 0, groundY);
    skyGradient.addColorStop(0, "#87CEEB");
    skyGradient.addColorStop(1, "#E0F6FF");
    ctx.fillStyle = skyGradient;
    ctx.fillRect(0, 0, width, groundY);
    
    // Çim/zemin
    ctx.fillStyle = "#22c55e";
    ctx.fillRect(0, groundY, width, height - groundY);
    
    // Çim çizgileri
    ctx.strokeStyle = "#16a34a";
    ctx.lineWidth = 2;
    for (let i = 0; i < width; i += 50) {
      ctx.beginPath();
      ctx.moveTo(i, groundY);
      ctx.lineTo(i, height);
      ctx.stroke();
    }
    
    // Sol kale (zemin seviyesinde)
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, groundY - goalH, goalW, goalH);
    ctx.fillStyle = "#1e3a5f";
    ctx.fillRect(2, groundY - goalH + 2, goalW - 4, goalH - 4);
    // Ağ çizgileri
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1;
    for (let i = 0; i <= goalH; i += 12) {
      ctx.beginPath();
      ctx.moveTo(2, groundY - goalH + i);
      ctx.lineTo(goalW - 2, groundY - goalH + i);
      ctx.stroke();
    }
    for (let i = 0; i <= goalW; i += 8) {
      ctx.beginPath();
      ctx.moveTo(2 + i, groundY - goalH);
      ctx.lineTo(2 + i, groundY);
      ctx.stroke();
    }
    
    // Sağ kale (zemin seviyesinde)
    ctx.fillStyle = "#fff";
    ctx.fillRect(width - goalW, groundY - goalH, goalW, goalH);
    ctx.fillStyle = "#1e3a5f";
    ctx.fillRect(width - goalW + 2, groundY - goalH + 2, goalW - 4, goalH - 4);
    // Ağ çizgileri
    for (let i = 0; i <= goalH; i += 12) {
      ctx.beginPath();
      ctx.moveTo(width - goalW + 2, groundY - goalH + i);
      ctx.lineTo(width - 2, groundY - goalH + i);
      ctx.stroke();
    }
    for (let i = 0; i <= goalW; i += 8) {
      ctx.beginPath();
      ctx.moveTo(width - goalW + 2 + i, groundY - goalH);
      ctx.lineTo(width - goalW + 2 + i, groundY);
      ctx.stroke();
    }
    
    // Zemin çizgisi
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(0, groundY);
    ctx.lineTo(width, groundY);
    ctx.stroke();
    
    // Oyuncu 1
    drawPlayer(ctx, state.player1, "P1");
    
    // Oyuncu 2
    drawPlayer(ctx, state.player2, state.player2.isPC ? "PC" : "P2");
    
    // Top
    ctx.beginPath();
    ctx.arc(state.ball.x, state.ball.y, state.ball.radius, 0, Math.PI * 2);
    ctx.fillStyle = "#fff";
    ctx.fill();
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Top deseni
    ctx.fillStyle = "#000";
    for (let i = 0; i < 5; i++) {
      const angle = (i * Math.PI * 2) / 5;
      const px = state.ball.x + Math.cos(angle) * 7;
      const py = state.ball.y + Math.sin(angle) * 7;
      ctx.beginPath();
      ctx.arc(px, py, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  };
  
  const drawPlayer = (ctx, player, label) => {
    // Vücut
    ctx.fillStyle = player.color;
    ctx.beginPath();
    ctx.arc(player.x, player.y, player.radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Kafa
    ctx.fillStyle = "#fcd5b5";
    ctx.beginPath();
    ctx.arc(player.x, player.y - player.radius * 0.3, player.radius * 0.55, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    
    // Saç
    ctx.fillStyle = player.hairColor;
    ctx.beginPath();
    ctx.arc(player.x, player.y - player.radius * 0.45, player.radius * 0.45, Math.PI, Math.PI * 2);
    ctx.fill();
    
    // Gözler
    ctx.fillStyle = "#000";
    ctx.beginPath();
    ctx.arc(player.x - 5, player.y - player.radius * 0.35, 2.5, 0, Math.PI * 2);
    ctx.arc(player.x + 5, player.y - player.radius * 0.35, 2.5, 0, Math.PI * 2);
    ctx.fill();
    
    // İsim
    ctx.fillStyle = "#fff";
    ctx.font = "bold 14px Arial";
    ctx.textAlign = "center";
    ctx.fillText(label, player.x, player.y + player.radius + 18);
  };

  const fetchData = async () => {
    try {
      const [balanceRes, gamesRes, waitingRes] = await Promise.all([
        axios.get(`${API}/credits/balance`),
        axios.get(`${API}/headball/my-games`),
        axios.get(`${API}/headball/waiting`)
      ]);
      
      setBalance(balanceRes.data.balance);
      setMyGames(gamesRes.data.games || []);
      setGameStats(gamesRes.data.stats || { wins: 0, losses: 0, draws: 0, total: 0 });
      setWaitingGames(waitingRes.data.games || []);
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartGame = (mode) => {
    setGameMode(mode);
    setShowSetupDialog(true);
  };

  const handleCreateGame = async () => {
    try {
      const response = await axios.post(`${API}/headball/create`, {
        isVsPC: gameMode === 'pc',
        playerConfig: playerConfig
      });
      
      if (response.data.success) {
        if (gameMode === 'pc') {
          // PC modunda hemen başla
          setCurrentGame(response.data.game);
          setShowSetupDialog(false);
          setGameStarted(true);
          setGameEnded(false);
          setScore({ player1: 0, player2: 0 });
          setGameTime(180);
          toast.success(isMobile ? "Oyun başladı!" : "Oyun başladı! Kontroller: W/A/D");
        } else {
          // Online modunda bekleme listesine ekle, oyun başlamasın
          setShowSetupDialog(false);
          toast.success("Oyun oluşturuldu! Rakip bekleniyor...");
          setActiveTab("online"); // Online sekmesine geç
        }
        
        if (refreshUser) refreshUser();
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Oyun başlatılamadı");
    }
  };

  const handleJoinGame = async (gameId) => {
    try {
      const response = await axios.post(`${API}/headball/${gameId}/join`, {
        playerConfig: playerConfig
      });
      
      if (response.data.success) {
        setCurrentGame(response.data.game);
        setGameStarted(true);
        setGameEnded(false);
        setScore({ player1: 0, player2: 0 });
        setGameTime(180);
        toast.success("Oyuna katıldınız!");
        
        if (refreshUser) refreshUser();
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Oyuna katılınamadı");
    }
  };

  const finishGame = async (gameId, p1Score, p2Score) => {
    if (gameEnded) return;
    setGameEnded(true);
    
    if (gameLoopRef.current) {
      cancelAnimationFrame(gameLoopRef.current);
    }
    
    try {
      const response = await axios.post(`${API}/headball/${gameId}/finish`, {
        player1Score: p1Score,
        player2Score: p2Score
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        if (refreshUser) refreshUser();
        fetchData();
        
        setTimeout(() => {
          setCurrentGame(null);
          setGameStarted(false);
          setGameEnded(false);
          setGameTime(180);
          setScore({ player1: 0, player2: 0 });
          gameStateRef.current = null;
        }, 3000);
      }
    } catch (error) {
      toast.error("Oyun sonucu kaydedilemedi");
      setGameEnded(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="container mx-auto px-4 py-6">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Gamepad2 className="h-8 w-8" />
                Kafa Topu
              </h1>
              <p className="text-purple-100 mt-1">
                Eğlenceli mini oyun - Kazan ve popülerlik kazan!
              </p>
            </div>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
              <p className="text-xs text-purple-100">Bakiye</p>
              <p className="text-xl font-bold flex items-center gap-1">
                <Coins className="h-4 w-4" />
                {balance}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Oyun ekranı */}
        {gameStarted && currentGame ? (
          <div className="max-w-4xl mx-auto">
            {/* Skor tablosu */}
            <Card className="mb-4 bg-slate-800 border-purple-500">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <p className="text-sm text-gray-400">{currentGame.player1Name}</p>
                    <p className="text-4xl font-bold text-blue-400">{score.player1}</p>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-400">Süre</p>
                    <p className="text-2xl font-bold text-white">{formatTime(gameTime)}</p>
                    <p className="text-xs text-gray-500">5 gole kadar</p>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-400">{currentGame.player2Name}</p>
                    <p className="text-4xl font-bold text-red-400">{score.player2}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Oyun canvas */}
            <div className="bg-slate-800 rounded-xl p-2 md:p-4 border border-purple-500">
              <canvas 
                ref={canvasRef}
                width={800}
                height={500}
                className="w-full rounded-lg border-2 border-green-600"
                style={{ maxWidth: '800px', margin: '0 auto', display: 'block' }}
                tabIndex={0}
              />
              
              {/* Mobil Kontroller - Oyun alanının dışında */}
              {isMobile && (
                <div className="mt-3 flex justify-between items-center px-2">
                  {/* Sol taraf - Hareket butonları */}
                  <div className="flex gap-3">
                    <button
                      className="w-14 h-14 bg-blue-500/40 border-2 border-blue-400/60 rounded-full flex items-center justify-center text-white/90 text-2xl font-bold active:bg-blue-600/60 select-none touch-none backdrop-blur-sm"
                      onTouchStart={() => handleTouchStart('a')}
                      onTouchEnd={() => handleTouchEnd('a')}
                      onMouseDown={() => handleTouchStart('a')}
                      onMouseUp={() => handleTouchEnd('a')}
                      onMouseLeave={() => handleTouchEnd('a')}
                    >
                      ◀
                    </button>
                    <button
                      className="w-14 h-14 bg-blue-500/40 border-2 border-blue-400/60 rounded-full flex items-center justify-center text-white/90 text-2xl font-bold active:bg-blue-600/60 select-none touch-none backdrop-blur-sm"
                      onTouchStart={() => handleTouchStart('d')}
                      onTouchEnd={() => handleTouchEnd('d')}
                      onMouseDown={() => handleTouchStart('d')}
                      onMouseUp={() => handleTouchEnd('d')}
                      onMouseLeave={() => handleTouchEnd('d')}
                    >
                      ▶
                    </button>
                  </div>
                  
                  {/* Sağ taraf - Zıplama butonu */}
                  <button
                    className="w-16 h-16 bg-green-500/40 border-2 border-green-400/60 rounded-full flex items-center justify-center text-white/90 text-sm font-bold active:bg-green-600/60 select-none touch-none backdrop-blur-sm"
                    onTouchStart={() => handleTouchStart('w')}
                    onTouchEnd={() => handleTouchEnd('w')}
                    onMouseDown={() => handleTouchStart('w')}
                    onMouseUp={() => handleTouchEnd('w')}
                    onMouseLeave={() => handleTouchEnd('w')}
                  >
                    ZIPLA
                  </button>
                </div>
              )}
            </div>
            
            {/* Kontroller - Sadece masaüstünde göster */}
            {!isMobile && (
            <Card className="mt-4 bg-slate-800 border-purple-500">
              <CardContent className="py-4">
                <div className="grid grid-cols-2 gap-8 text-center text-white">
                  <div>
                    <p className="font-bold text-blue-400 mb-2">Oyuncu 1 (Sol)</p>
                    <p className="text-sm"><kbd className="bg-slate-700 px-2 py-1 rounded">W</kbd> Zıpla</p>
                    <p className="text-sm"><kbd className="bg-slate-700 px-2 py-1 rounded">A</kbd> Sol <kbd className="bg-slate-700 px-2 py-1 rounded">D</kbd> Sağ</p>
                  </div>
                  <div>
                    <p className="font-bold text-red-400 mb-2">
                      {currentGame.isVsPC ? "PC (Otomatik)" : "Oyuncu 2 (Sağ)"}
                    </p>
                    {!currentGame.isVsPC && (
                      <>
                        <p className="text-sm"><kbd className="bg-slate-700 px-2 py-1 rounded">↑</kbd> Zıpla</p>
                        <p className="text-sm"><kbd className="bg-slate-700 px-2 py-1 rounded">←</kbd> Sol <kbd className="bg-slate-700 px-2 py-1 rounded">→</kbd> Sağ</p>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {gameEnded && (
              <div className="mt-4 text-center">
                <p className="text-white text-xl">Oyun bitti! Sonuçlar kaydediliyor...</p>
              </div>
            )}
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-4xl mx-auto">
            <TabsList className="grid w-full grid-cols-3 mb-6 bg-slate-800">
              <TabsTrigger value="play" className="data-[state=active]:bg-purple-600">
                <Play className="h-4 w-4 mr-2" />
                Oyna
              </TabsTrigger>
              <TabsTrigger value="online" className="data-[state=active]:bg-purple-600">
                <Users className="h-4 w-4 mr-2" />
                Online
              </TabsTrigger>
              <TabsTrigger value="history" className="data-[state=active]:bg-purple-600">
                <History className="h-4 w-4 mr-2" />
                Geçmiş
              </TabsTrigger>
            </TabsList>

            {/* Oyna Tab */}
            <TabsContent value="play">
              <div className="grid md:grid-cols-2 gap-6">
                {/* PC'ye Karşı */}
                <Card className="bg-slate-800 border-purple-500 hover:border-purple-400 transition-colors cursor-pointer"
                  onClick={() => handleStartGame('pc')}>
                  <CardHeader>
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-3">
                      <Monitor className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-center text-white">PC&apos;ye Karşı</CardTitle>
                    <CardDescription className="text-center text-gray-400">
                      Yapay zeka rakibe karşı oyna
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-center">
                    <Badge className="bg-purple-600">2 Kredi</Badge>
                    <p className="text-sm text-gray-400 mt-2">Kazanana +5 Popülerlik</p>
                  </CardContent>
                </Card>
                
                {/* Online */}
                <Card className="bg-slate-800 border-purple-500 hover:border-purple-400 transition-colors cursor-pointer"
                  onClick={() => handleStartGame('online')}>
                  <CardHeader>
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center mb-3">
                      <Users className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-center text-white">Online Oyun</CardTitle>
                    <CardDescription className="text-center text-gray-400">
                      Gerçek oyuncuya karşı oyna
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-center">
                    <Badge className="bg-purple-600">2 Kredi</Badge>
                    <p className="text-sm text-gray-400 mt-2">Kazanana +5 Popülerlik</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* İstatistikler */}
              <Card className="mt-6 bg-slate-800 border-purple-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                    İstatistiklerim
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div className="p-3 bg-slate-700 rounded-lg">
                      <p className="text-2xl font-bold text-green-400">{gameStats.wins}</p>
                      <p className="text-sm text-gray-400">Galibiyet</p>
                    </div>
                    <div className="p-3 bg-slate-700 rounded-lg">
                      <p className="text-2xl font-bold text-red-400">{gameStats.losses}</p>
                      <p className="text-sm text-gray-400">Mağlubiyet</p>
                    </div>
                    <div className="p-3 bg-slate-700 rounded-lg">
                      <p className="text-2xl font-bold text-gray-400">{gameStats.draws}</p>
                      <p className="text-sm text-gray-400">Berabere</p>
                    </div>
                    <div className="p-3 bg-slate-700 rounded-lg">
                      <p className="text-2xl font-bold text-purple-400">{gameStats.total}</p>
                      <p className="text-sm text-gray-400">Toplam</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Kurallar */}
              <Card className="mt-6 bg-slate-800/50 border-purple-500/50">
                <CardHeader>
                  <CardTitle className="text-white">Nasıl Oynanır?</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-300 space-y-2">
                  <p>⚽ 3 dakika süre veya 5 gole kadar oynanır</p>
                  <p>🎮 Oyuncu 1: <kbd className="bg-slate-700 px-1 rounded">W</kbd> zıpla, <kbd className="bg-slate-700 px-1 rounded">A</kbd>/<kbd className="bg-slate-700 px-1 rounded">D</kbd> sol/sağ</p>
                  <p>🎮 Oyuncu 2: <kbd className="bg-slate-700 px-1 rounded">↑</kbd> zıpla, <kbd className="bg-slate-700 px-1 rounded">←</kbd>/<kbd className="bg-slate-700 px-1 rounded">→</kbd> sol/sağ</p>
                  <p>🏆 Kazanan +5 popülerlik puanı alır</p>
                  <p>💰 Her oyun 2 kredi</p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Online Tab */}
            <TabsContent value="online">
              <Card className="bg-slate-800 border-purple-500">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Zap className="h-5 w-5 text-yellow-500" />
                    Bekleyen Oyunlar
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Katılmak için bir oyun seçin veya yeni oluşturun
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Kendi bekleyen oyunum varsa göster */}
                  {waitingGames.some(g => g.player1Id === user?.id) && (
                    <div className="mb-4 p-4 bg-purple-900/50 rounded-lg border border-purple-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">🎮 Oyununuz oluşturuldu!</p>
                          <p className="text-sm text-purple-300">Rakip bekleniyor...</p>
                        </div>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-400 hover:bg-red-500/20"
                          onClick={async () => {
                            const myGame = waitingGames.find(g => g.player1Id === user?.id);
                            if (myGame) {
                              try {
                                await axios.delete(`${API}/headball/${myGame.id}/cancel`);
                                toast.success("Oyun iptal edildi");
                                fetchData();
                              } catch (error) {
                                toast.error("İptal edilemedi");
                              }
                            }
                          }}
                        >
                          İptal Et
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Diğer bekleyen oyunlar */}
                  {waitingGames.filter(g => g.player1Id !== user?.id).length === 0 && !waitingGames.some(g => g.player1Id === user?.id) ? (
                    <div className="text-center py-8 text-gray-400">
                      <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Bekleyen oyun yok</p>
                      <Button 
                        className="mt-4 bg-purple-600 hover:bg-purple-700"
                        onClick={() => handleStartGame('online')}
                      >
                        Yeni Oyun Oluştur
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {waitingGames.filter(g => g.player1Id !== user?.id).map((game) => (
                        <div 
                          key={game.id}
                          className="flex items-center justify-between p-4 bg-slate-700 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                              <span className="text-white font-bold">
                                {game.player1Name?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-white">{game.player1Name}</p>
                              <p className="text-sm text-gray-400">Rakip bekliyor...</p>
                            </div>
                          </div>
                          <Button 
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleJoinGame(game.id)}
                          >
                            Katıl (2💰)
                          </Button>
                        </div>
                      ))}
                      
                      {/* Kendi oyunu yoksa oluştur butonu göster */}
                      {!waitingGames.some(g => g.player1Id === user?.id) && (
                        <div className="text-center pt-4 border-t border-slate-600">
                          <Button 
                            className="bg-purple-600 hover:bg-purple-700"
                            onClick={() => handleStartGame('online')}
                          >
                            Yeni Oyun Oluştur
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Geçmiş Tab */}
            <TabsContent value="history">
              <Card className="bg-slate-800 border-purple-500">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Oyun Geçmişi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {myGames.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Gamepad2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Henüz oyun oynamadınız</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {myGames.slice(0, 10).map((game) => {
                        const isPlayer1 = game.player1Id === user?.id;
                        const myScore = isPlayer1 ? game.player1Score : game.player2Score;
                        const opponentScore = isPlayer1 ? game.player2Score : game.player1Score;
                        const opponentName = isPlayer1 ? game.player2Name : game.player1Name;
                        const won = game.winnerId === user?.id;
                        const draw = !game.winnerId && game.status === 'finished';
                        
                        return (
                          <div 
                            key={game.id}
                            className={`flex items-center justify-between p-4 rounded-lg ${
                              won ? 'bg-green-900/30 border border-green-500/30' :
                              draw ? 'bg-gray-700/30 border border-gray-500/30' :
                              'bg-red-900/30 border border-red-500/30'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                won ? 'bg-green-500' : draw ? 'bg-gray-500' : 'bg-red-500'
                              }`}>
                                {won ? <Trophy className="h-5 w-5 text-white" /> :
                                 draw ? <span className="text-white">=</span> :
                                 <span className="text-white">✕</span>}
                              </div>
                              <div>
                                <p className="font-medium text-white">vs {opponentName}</p>
                                <p className="text-sm text-gray-400">
                                  {new Date(game.createdAt).toLocaleDateString('tr-TR')}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-white">
                                {myScore} - {opponentScore}
                              </p>
                              <Badge className={
                                won ? 'bg-green-600' : draw ? 'bg-gray-600' : 'bg-red-600'
                              }>
                                {won ? 'Galibiyet' : draw ? 'Berabere' : 'Mağlubiyet'}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Karakter Ayarları Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="bg-slate-800 border-purple-500 text-white">
          <DialogHeader>
            <DialogTitle>Karakterini Özelleştir</DialogTitle>
            <DialogDescription className="text-gray-400">
              {gameMode === 'pc' ? "PC'ye karşı oynayacaksın" : "Online rakip bulunacak"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Saç Rengi */}
            <div>
              <Label className="text-white mb-2 block">Saç Rengi</Label>
              <div className="flex flex-wrap gap-2">
                {HAIR_COLORS.map((hair) => (
                  <button
                    key={hair.id}
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      playerConfig.hairColor === hair.color 
                        ? 'border-purple-500 scale-110' 
                        : 'border-transparent'
                    }`}
                    style={{ backgroundColor: hair.color }}
                    onClick={() => setPlayerConfig({ ...playerConfig, hairColor: hair.color })}
                    title={hair.name}
                  />
                ))}
              </div>
            </div>
            
            {/* Forma Rengi */}
            <div>
              <Label className="text-white mb-2 block">Forma Rengi</Label>
              <div className="flex flex-wrap gap-2">
                {SHIRT_COLORS.map((shirt) => (
                  <button
                    key={shirt.id}
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      playerConfig.shirtColor === shirt.color 
                        ? 'border-purple-500 scale-110' 
                        : 'border-transparent'
                    } ${shirt.id === 'white' ? 'border border-gray-400' : ''}`}
                    style={{ backgroundColor: shirt.color }}
                    onClick={() => setPlayerConfig({ ...playerConfig, shirtColor: shirt.color })}
                    title={shirt.name}
                  />
                ))}
              </div>
            </div>
            
            {/* Önizleme */}
            <div className="flex justify-center">
              <div className="w-24 h-24 rounded-full relative" style={{ backgroundColor: playerConfig.shirtColor }}>
                <div 
                  className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full"
                  style={{ backgroundColor: '#fcd5b5' }}
                >
                  <div 
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-5 rounded-t-full"
                    style={{ backgroundColor: playerConfig.hairColor }}
                  />
                </div>
              </div>
            </div>
            
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700"
              onClick={handleCreateGame}
            >
              <Play className="h-4 w-4 mr-2" />
              Oyuna Başla (2💰)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HeadBallPage;
